import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, CloudSun, Heart, Mic, Sparkles, Video, X } from "lucide-react";
import { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SceneType, sceneConfig } from "@/hooks/useScenes";
import GoLiveModal from "@/components/global-connect/GoLiveModal";
import { useAuthContext } from "@/contexts/AuthContext";
import { useGlobalConnect } from "@/hooks/useGlobalConnect";
import { useGlobalIntents } from "@/hooks/useGlobalIntents";
import { GlobalConnectLimitsBar } from "@/components/global-connect/GlobalConnectLimitsBar";
import GlobalMemoryLogs from "@/components/global-connect/GlobalMemoryLogs";
import EchoMemoryModal from "@/components/global-connect/EchoMemoryModal";
import SpeakQueue from "@/components/global-connect/SpeakQueue";
import { toast } from "sonner";

type Mode = "ambient" | "veil" | "video";

type PresencePayload = {
  id: string;
  intents?: string[];
  tzOffsetMin?: number;
  activityTag?: "morning_coffee" | "evening_walk" | "weekend_downtime" | "late_night";
  aura?: "curious" | "dating" | "serious";
  online_at: string;
};

type SceneSilhouette = {
  id: string;
  aura: "curious" | "dating" | "serious";
  activityTag?: PresencePayload["activityTag"];
  tzOffsetMin?: number;
};

const AURA = {
  curious: { label: "Global Curious", ring: "ring-orange-400/60", glow: "bg-orange-400/15", dot: "bg-orange-400" },
  dating: { label: "Global Dating", ring: "ring-rose-500/60", glow: "bg-rose-500/15", dot: "bg-rose-500" },
  serious: { label: "Global Serious", ring: "ring-blue-600/60", glow: "bg-blue-600/15", dot: "bg-blue-600" },
} as const;

function pickAura(intents: string[] | undefined): SceneSilhouette["aura"] {
  const set = new Set((intents || []).map((x) => x.toLowerCase()));
  if (set.has("global_dating")) return "dating";
  if (set.has("global_serious")) return "serious";
  return "curious";
}

function localHourForOffset(offsetMin: number | undefined) {
  const base = new Date();
  const utcMs = base.getTime() + base.getTimezoneOffset() * 60_000;
  const localMs = utcMs + (offsetMin ?? 0) * 60_000;
  return new Date(localMs).getHours();
}

function activityTagFromHour(h: number): PresencePayload["activityTag"] {
  if (h >= 6 && h <= 11) return "morning_coffee";
  if (h >= 17 && h <= 20) return "evening_walk";
  if (h >= 21 || h <= 1) return "late_night";
  return "weekend_downtime";
}

function intersects(a: string[] | undefined, b: string[] | undefined) {
  if (!a?.length || !b?.length) return false;
  const setA = new Set(a);
  return b.some((x) => setA.has(x));
}

interface SceneArenaViewProps {
  countryCode: string;
  countryName: string;
  countryFlag: string;
  cityId: string;
  cityName: string;
  scene: SceneType;
  onBack: () => void;
  onPopUser: (userId: string) => void;
}

const SceneArenaView: React.FC<SceneArenaViewProps> = ({
  countryCode,
  countryName,
  countryFlag,
  cityId,
  cityName,
  scene,
  onBack,
  onPopUser,
}) => {
  const { user, profile } = useAuthContext();
  const { limits, useAction } = useGlobalConnect();
  const { selectedIntents } = useGlobalIntents();
  const sceneInfo = sceneConfig[scene];

  const channelRef = useRef<RealtimeChannel | null>(null);
  const mountedRef = useRef(false);

  const [mode, setMode] = useState<Mode>("ambient");
  const [veilSecondsLeft, setVeilSecondsLeft] = useState<number>(0);
  const [showGoLive, setShowGoLive] = useState(false);
  const [myStream, setMyStream] = useState<MediaStream | null>(null);
  const myStreamRef = useRef<MediaStream | null>(null);
  myStreamRef.current = myStream;
  
  // New features state
  const [showMemoryLogs, setShowMemoryLogs] = useState(false);
  const [showEchoMemory, setShowEchoMemory] = useState(false);
  const [showSpeakQueue, setShowSpeakQueue] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [currentSpeakerId, setCurrentSpeakerId] = useState<string | null>(null);
  const [lastInteraction, setLastInteraction] = useState<{ type: "pop" | "match" | "resonance"; userId?: string } | null>(null);

  const [presence, setPresence] = useState<Record<string, PresencePayload[]>>({});
  const [sparkCardUserId, setSparkCardUserId] = useState<string | null>(null);

  // Ambient audio (best-effort): use a silent fallback if blocked / missing.
  const audioSrc = useMemo(() => {
    // You can swap these to your own hosted assets later.
    switch (scene) {
      case "beach":
        return "https://cdn.pixabay.com/download/audio/2022/03/15/audio_b9f07a6a0b.mp3?filename=sea-waves-ambient-110397.mp3";
      case "cafe":
        return "https://cdn.pixabay.com/download/audio/2021/11/09/audio_6c3cdd43aa.mp3?filename=cafe-ambience-ambient-7251.mp3";
      case "rooftop":
        return "https://cdn.pixabay.com/download/audio/2021/08/04/audio_5a3d11d3c0.mp3?filename=wind-ambient-6195.mp3";
      case "night_city":
      default:
        return "https://cdn.pixabay.com/download/audio/2022/02/23/audio_333f0cb1cf.mp3?filename=city-ambience-101189.mp3";
    }
  }, [scene]);

  // Join realtime presence for silhouettes
  useEffect(() => {
    if (mountedRef.current) return;
    if (!user || !profile) return;
    mountedRef.current = true;

    const channelName = `scene-arena:${countryCode}:${cityId}:${scene}`;
    const channel = supabase.channel(channelName);

    channel.on("presence", { event: "sync" }, () => {
      setPresence(channel.presenceState() as any);
    });

    channel.subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        const tzOffsetMin = -new Date().getTimezoneOffset(); // minutes from UTC
        const activityTag = activityTagFromHour(localHourForOffset(tzOffsetMin));

        await channel.track({
          id: user.id,
          intents: selectedIntents,
          tzOffsetMin,
          activityTag,
          aura: pickAura(selectedIntents),
          online_at: new Date().toISOString(),
        } satisfies PresencePayload);
      }
    });

    channelRef.current = channel;
    return () => {
      if (myStreamRef.current) {
        myStreamRef.current.getTracks().forEach((t) => t.stop());
        myStreamRef.current = null;
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      mountedRef.current = false;
    };
  }, [user, profile, countryCode, cityId, scene, selectedIntents]);

  // Flatten presence -> silhouettes (filter by overlapping intents only)
  const silhouettes = useMemo(() => {
    const all: PresencePayload[] = [];
    Object.values(presence).forEach((arr) => {
      (arr || []).forEach((p) => all.push(p));
    });

    const others = all.filter((p) => p.id !== user?.id);
    const overlapped = others.filter((p) => intersects(p.intents, selectedIntents));

    const mapped: SceneSilhouette[] = overlapped.map((p) => ({
      id: p.id,
      aura: p.aura || pickAura(p.intents),
      tzOffsetMin: p.tzOffsetMin,
      activityTag: p.activityTag,
    }));

    // display 8-12 max, stable-ish
    return mapped.slice(0, 12);
  }, [presence, selectedIntents, user?.id]);

  const overlapPercent = useMemo(() => {
    const base = 68;
    const extra = Math.min(30, silhouettes.length * 3);
    return Math.min(98, base + extra);
  }, [silhouettes.length]);

  const goldenHour = useMemo(() => {
    // "Golden hour" if many users + local time is late afternoon/evening for majority
    if (silhouettes.length >= 6) return true;
    const hours = silhouettes.map((s) => localHourForOffset(s.tzOffsetMin));
    const peak = hours.filter((h) => h >= 18 && h <= 21).length;
    return peak >= 4;
  }, [silhouettes]);

  const resonanceText = useMemo(() => {
    if (!silhouettes.length) return null;
    const myOffset = -new Date().getTimezoneOffset();
    const myTag = activityTagFromHour(localHourForOffset(myOffset));
    const match = silhouettes.find((s) => (s.activityTag || "weekend_downtime") === myTag);
    if (!match) return null;
    switch (myTag) {
      case "morning_coffee":
        return "Sharing morning coffee rhythm with someone nearby in the world.";
      case "evening_walk":
        return "Sharing evening walk rhythm with someone nearby in the world.";
      case "late_night":
        return "Sharing late night glow rhythm with someone nearby in the world.";
      default:
        return "Sharing a calm downtime rhythm with someone nearby in the world.";
    }
  }, [silhouettes]);

  const weatherLine = useMemo(() => {
    const sceneName = sceneInfo?.label || "Scene";
    const city = `${cityName} ${sceneName}`;
    const vibe = silhouettes.length >= 6 ? "High Connection Humidity" : "Breezy with Conversation";
    return `${city}: ${vibe} (${overlapPercent}% Open to Chat) • Chance of Cultural Sparks`;
  }, [sceneInfo?.label, cityName, silhouettes.length, overlapPercent]);

  const handleStepForward = useCallback(() => {
    setMode("veil");
    setVeilSecondsLeft(45);
  }, []);

  // Observer veil countdown
  useEffect(() => {
    if (mode !== "veil") return;
    if (veilSecondsLeft <= 0) {
      setMode("video");
      setShowGoLive(true);
      return;
    }
    const t = window.setTimeout(() => setVeilSecondsLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [mode, veilSecondsLeft]);

  const handleGoLive = useCallback(
    async (stream: MediaStream) => {
      setMyStream(stream);
      setShowGoLive(false);
      // Mark myself as "video active" for others (best-effort)
      if (channelRef.current && user) {
        const tzOffsetMin = -new Date().getTimezoneOffset();
        const activityTag = activityTagFromHour(localHourForOffset(tzOffsetMin));
        await channelRef.current.track({
          id: user.id,
          intents: selectedIntents,
          tzOffsetMin,
          activityTag,
          aura: pickAura(selectedIntents),
          online_at: new Date().toISOString(),
          is_video_active: true,
        } as any);
      }
    },
    [selectedIntents, user]
  );

  const handleLeave = useCallback(() => {
    onBack();
  }, [onBack]);

  // Spark Chain Reactions - notify nearby compatible auras (defined before handlePop)
  const triggerSparkChainReaction = useCallback(
    async (targetUserId: string, interactionType: "pop" | "match" | "resonance") => {
      if (!user || !channelRef.current) return;

      try {
        // Find 3-5 nearby compatible users in the same scene
        const state = channelRef.current.presenceState();
        const compatibleUsers: string[] = [];

        Object.values(state).forEach((presences: unknown[]) => {
          (presences as Array<{ id: string; intents?: string[] }>).forEach((p) => {
            if (
              p.id !== user.id &&
              p.id !== targetUserId &&
              intersects(p.intents, selectedIntents) &&
              compatibleUsers.length < 5
            ) {
              compatibleUsers.push(p.id);
            }
          });
        });

        // Send gentle wave notifications
        for (const userId of compatibleUsers.slice(0, 3)) {
          await supabase.from("notifications").insert({
            user_id: userId,
            type: "spark_chain",
            title: "✨ Spark Chain Reaction",
            body: `A ${interactionType === "match" ? "match" : "connection"} happened nearby in ${cityName}—want to join the vibe?`,
            data: {
              country_code: countryCode,
              city_id: cityId,
              scene: scene,
              interaction_type: interactionType,
            },
          });
        }

        if (compatibleUsers.length > 0) {
          // Silent notification - don't spam toast
          console.log(`✨ Sent gentle waves to ${compatibleUsers.length} nearby compatible auras`);
        }
      } catch (error) {
        console.error("Error triggering spark chain:", error);
      }
    },
    [user, selectedIntents, cityName, countryCode, cityId, scene]
  );

  const handlePop = useCallback(
    async (targetUserId: string) => {
      if (!user) return;
      const ok = await useAction("pop");
      if (!ok) return;
      
      // Check for mutual pop (match)
      const { data: mutualPop } = await supabase
        .from("pops")
        .select("id")
        .eq("popper_id", targetUserId)
        .eq("popped_id", user.id)
        .maybeSingle();

      onPopUser(targetUserId);
      
      if (mutualPop) {
        setLastInteraction({ type: "match", userId: targetUserId });
        triggerSparkChainReaction(targetUserId, "match");
        toast.success("🎉 It's a match!");
      } else {
        setLastInteraction({ type: "pop", userId: targetUserId });
        triggerSparkChainReaction(targetUserId, "pop");
      }
      
      // Offer Echo Memory after a short delay
      setTimeout(() => {
        setShowEchoMemory(true);
      }, 2000);
    },
    [onPopUser, useAction, user, triggerSparkChainReaction]
  );

  // Host management
  useEffect(() => {
    if (!channelRef.current || !user || mode !== "video") return;

    // First user in video mode becomes host
    const state = channelRef.current.presenceState();
    const videoUsers = Object.values(state).flat().filter(
      (p: any) => p.is_video_active && p.id !== user.id
    );

    if (videoUsers.length === 0) {
      setIsHost(true);
      setCurrentSpeakerId(user.id);
    } else if (!isHost && videoUsers.length > 0) {
      // Find the first video user as host
      const firstVideoUser = videoUsers[0] as any;
      if (firstVideoUser.id && !currentSpeakerId) {
        setCurrentSpeakerId(firstVideoUser.id);
      }
    }
  }, [mode, user, isHost, currentSpeakerId]);

  // Request to speak handlers
  const handleRequestSpeak = useCallback(() => {
    if (!channelRef.current || !user) return;
    const currentPresence = channelRef.current.presenceState()[user.id]?.[0] as any;
    if (currentPresence) {
      channelRef.current.track({
        ...currentPresence,
        speak_requested_at: new Date().toISOString(),
      } as any);
    }
  }, [user]);

  const handleCancelRequest = useCallback(() => {
    if (!channelRef.current || !user) return;
    const currentPresence = channelRef.current.presenceState()[user.id]?.[0] as any;
    if (currentPresence) {
      const { speak_requested_at, ...rest } = currentPresence;
      channelRef.current.track(rest);
    }
  }, [user]);

  const handleGrantSpeak = useCallback(
    (userId: string) => {
      if (!channelRef.current || !isHost) return;
      setCurrentSpeakerId(userId);
      const userPresence = channelRef.current.presenceState()[userId]?.[0] as any;
      if (userPresence) {
        channelRef.current.track({
          ...userPresence,
          is_speaking: true,
          speak_requested_at: undefined,
        } as any);
      }
      toast.success(`Granted mic to ${userPresence?.name || "user"}`);
    },
    [isHost]
  );

  const handleRevokeSpeak = useCallback(
    (userId: string) => {
      if (!channelRef.current || !isHost) return;
      setCurrentSpeakerId(null);
      const userPresence = channelRef.current.presenceState()[userId]?.[0] as any;
      if (userPresence) {
        channelRef.current.track({
          ...userPresence,
          is_speaking: false,
        } as any);
      }
    },
    [isHost]
  );

  const bgClass = useMemo(() => {
    switch (scene) {
      case "beach":
        return "from-sky-200 via-blue-100 to-rose-100";
      case "cafe":
        return "from-amber-100 via-orange-50 to-rose-100";
      case "rooftop":
        return "from-indigo-100 via-slate-100 to-sky-100";
      case "night_city":
      default:
        return "from-slate-950 via-indigo-950 to-fuchsia-950";
    }
  }, [scene]);

  const particleClass = useMemo(() => {
    switch (scene) {
      case "beach":
        return "opacity-20";
      case "cafe":
        return "opacity-25";
      case "rooftop":
        return "opacity-20";
      case "night_city":
      default:
        return "opacity-30";
    }
  }, [scene]);

  return (
    <div className={cn("min-h-screen w-full relative overflow-hidden bg-gradient-to-br", bgClass)}>
      {/* Ambient audio (best-effort) */}
      <audio src={audioSrc} autoPlay loop preload="auto" className="hidden" />

      {/* Subtle visuals */}
      <div className={cn("pointer-events-none absolute inset-0", particleClass)}>
        <div className="absolute inset-0 [background:radial-gradient(circle_at_20%_30%,rgba(255,255,255,0.35),transparent_40%),radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.25),transparent_45%),radial-gradient(circle_at_60%_80%,rgba(255,255,255,0.18),transparent_45%)]" />
        <div className="absolute inset-0 animate-pulse [background:radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.10),transparent_55%)]" />
      </div>

      {/* Top bar */}
      <div className="relative z-10 px-4 pt-4">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={handleLeave} className="bg-white/30 hover:bg-white/40">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="text-center">
            <div className="text-sm font-semibold">
              {countryFlag} {countryName}
            </div>
            <div className="text-xs text-muted-foreground">
              {cityName} • {sceneInfo?.label}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSparkCardUserId(null)}
            className="bg-white/30 hover:bg-white/40"
            title="Close"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Limits */}
        {limits && (
          <div className="mt-3">
            <GlobalConnectLimitsBar limits={limits} />
          </div>
        )}

        {/* Connection Weather */}
        <div className="mt-3 p-3 rounded-2xl border border-white/30 bg-white/35 backdrop-blur-md">
          <div className="flex items-start gap-2">
            <CloudSun className="w-5 h-5 text-foreground/80 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-foreground">Connection Weather</div>
              <div className="text-xs text-foreground/70 mt-0.5">{weatherLine}</div>
              {goldenHour && (
                <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded-full bg-yellow-400/20 border border-yellow-400/30 text-xs font-medium text-foreground">
                  <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
                  Golden Hour • peak synchronized activity
                </div>
              )}
              {resonanceText && (
                <div className="mt-2 text-xs text-foreground/70 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" />
                  {resonanceText}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main stage */}
      <div className="relative z-10 px-4 pt-5 pb-24">
        {/* Ambient Mode */}
        {(mode === "ambient" || mode === "veil") && (
          <div className="relative h-[68vh] rounded-3xl border border-white/25 bg-white/15 backdrop-blur-sm overflow-hidden">
            {/* silhouettes */}
            <div className="absolute inset-0">
              {silhouettes.map((s, idx) => {
                const row = Math.floor(idx / 4);
                const col = idx % 4;
                const top = 18 + row * 22 + (col % 2) * 4;
                const left = 10 + col * 22 + (row % 2) * 3;
                const aura = AURA[s.aura];
                return (
                  <button
                    key={s.id}
                    onClick={() => setSparkCardUserId(s.id)}
                    className={cn(
                      "absolute w-16 h-16 rounded-full",
                      "bg-black/20 backdrop-blur-sm",
                      "ring-2",
                      aura.ring,
                      "transition-transform hover:scale-[1.03]"
                    )}
                    style={{ top: `${top}%`, left: `${left}%` }}
                    title={aura.label}
                  >
                    <div className={cn("absolute inset-0 rounded-full", aura.glow)} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-9 h-9 rounded-full bg-white/10" />
                    </div>
                    <div className={cn("absolute -bottom-1 -right-1 w-4 h-4 rounded-full border border-white/30", aura.dot)} />
                  </button>
                );
              })}
            </div>

            {/* Empty state */}
            {silhouettes.length === 0 && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <div className="text-3xl mb-2">{sceneInfo?.emoji}</div>
                <div className="font-semibold">Ambient Mode</div>
                <div className="text-sm text-muted-foreground mt-1">
                  No overlapping-intent silhouettes are here yet. Stay a moment—new arrivals will appear.
                </div>
              </div>
            )}

            {/* Veil overlay */}
            {mode === "veil" && (
              <div className="absolute inset-0 bg-black/35 backdrop-blur-sm flex flex-col items-center justify-center text-center px-6">
                <div className="text-white font-semibold text-lg">Observer Veil</div>
                <div className="text-white/80 text-sm mt-1">
                  You’re anonymous while you observe. Revealing soon…
                </div>
                <div className="mt-4 text-4xl font-bold text-white tabular-nums">{veilSecondsLeft}s</div>
                <div className="mt-4 text-white/70 text-xs">
                  Tip: tap a silhouette for a Cultural Spark icebreaker.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Video Mode (lightweight MVP) */}
        {mode === "video" && (
          <div className="relative h-[68vh] rounded-3xl border border-white/25 bg-white/15 backdrop-blur-sm overflow-hidden">
            {/* Speak Queue Sidebar */}
            {showSpeakQueue && (
              <div className="absolute right-0 top-0 bottom-0 w-80 bg-white/95 backdrop-blur-md border-l border-border p-4 z-20 overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Speak Queue</h3>
                  <Button variant="ghost" size="icon" onClick={() => setShowSpeakQueue(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <SpeakQueue
                  channel={channelRef.current}
                  isHost={isHost}
                  currentSpeakerId={currentSpeakerId}
                  onRequestSpeak={handleRequestSpeak}
                  onCancelRequest={handleCancelRequest}
                  onGrantSpeak={handleGrantSpeak}
                  onRevokeSpeak={handleRevokeSpeak}
                />
              </div>
            )}

            <div className="absolute inset-0 p-4 grid grid-cols-2 gap-3">
              {/* Local tile */}
              <div className="relative rounded-2xl overflow-hidden bg-black/40 border border-white/15">
                {myStream ? (
                  <video
                    autoPlay
                    playsInline
                    muted
                    ref={(el) => {
                      if (el && myStream) el.srcObject = myStream;
                    }}
                    className="w-full h-full object-cover scale-x-[-1]"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/80">
                    <div className="text-center">
                      <Video className="w-8 h-8 mx-auto mb-2" />
                      <div className="text-sm font-medium">Go live to join</div>
                      <div className="text-xs text-white/70 mt-1">Camera/mic are opt-in</div>
                      <Button className="mt-3" onClick={() => setShowGoLive(true)}>
                        Enable Camera
                      </Button>
                    </div>
                  </div>
                )}
                <div className="absolute top-2 left-2 text-xs px-2 py-1 rounded-full bg-black/50 text-white">
                  You
                </div>
              </div>

              {/* Others tiles (silhouettes; tap to pop) */}
              {silhouettes.slice(0, 3).map((s) => {
                const aura = AURA[s.aura];
                return (
                  <button
                    key={s.id}
                    onClick={() => handlePop(s.id)}
                    className={cn(
                      "relative rounded-2xl overflow-hidden border border-white/15 bg-black/30 text-left",
                      "ring-2",
                      aura.ring
                    )}
                    title="Tap balloon pop"
                  >
                    <div className={cn("absolute inset-0", aura.glow)} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-white/10" />
                    </div>
                    <div className="absolute top-2 left-2 flex items-center gap-2 text-xs px-2 py-1 rounded-full bg-black/50 text-white">
                      <span className={cn("w-2 h-2 rounded-full", aura.dot)} />
                      {AURA[s.aura].label}
                    </div>
                    {/* balloon */}
                    <div className="absolute -top-2 -right-1">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 shadow-lg animate-bounce" />
                    </div>
                    <div className="absolute bottom-2 left-2 text-[11px] text-white/80">
                      Tap balloon to Pop
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Floating actions */}
        {mode === "ambient" && (
          <div className="fixed right-4 bottom-28 z-20 flex flex-col items-end gap-2">
            <Button
              onClick={handleStepForward}
              className="rounded-full h-12 px-5 shadow-lg bg-white text-black hover:bg-white/90"
            >
              <Video className="w-4 h-4 mr-2" />
              Step Forward to Video
            </Button>
            <button
              onClick={() => setMode("ambient")}
              className="text-xs text-white/80 bg-black/30 backdrop-blur px-3 py-1.5 rounded-full border border-white/20"
            >
              Stay in Ambient
            </button>
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 z-30">
        <div className="mx-auto max-w-xl px-4 pb-4">
          <div className="h-16 rounded-2xl bg-black/40 backdrop-blur-md border border-white/15 flex items-center justify-around text-white">
            <button className="flex flex-col items-center gap-1 text-xs" onClick={() => setMode("ambient")}>
              🎈
              <span>Pop</span>
            </button>
            <button
              className="flex flex-col items-center gap-1 text-xs"
              onClick={() => {
                if (mode === "video") {
                  setShowSpeakQueue(!showSpeakQueue);
                } else {
                  setShowGoLive(true);
                }
              }}
            >
              <Mic className="w-5 h-5" />
              <span>{mode === "video" ? "Queue" : "Mic"}</span>
            </button>
            <button className="flex flex-col items-center gap-1 text-xs" onClick={handleLeave}>
              <span className="text-lg">⏎</span>
              <span>Leave</span>
            </button>
            <button className="flex flex-col items-center gap-1 text-xs" onClick={() => setShowMemoryLogs(true)}>
              <Heart className="w-5 h-5" />
              <span>Memories</span>
            </button>
          </div>
        </div>
      </div>

      {/* Cultural Spark AI nudge (MVP card) */}
      {sparkCardUserId && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-end justify-center">
          <div className="w-full max-w-xl rounded-t-3xl bg-white p-4 border-t border-border">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">Cultural Spark</div>
                <div className="text-xs text-muted-foreground mt-1">
                  A low-pressure nudge tied to your shared rhythm + intent.
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSparkCardUserId(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="mt-3 p-3 rounded-2xl bg-muted/40">
              <div className="text-sm">
                “Both in a similar rhythm right now—want an easy opener?”
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                Suggestion: Ask about their local moment (coffee, walk, or late-night vibe) and share one detail from yours.
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Button className="flex-1" onClick={() => { setSparkCardUserId(null); }}>
                Send Opener
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setSparkCardUserId(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Go Live modal */}
      <GoLiveModal
        isOpen={showGoLive}
        onClose={() => setShowGoLive(false)}
        onGoLive={handleGoLive}
        sceneEmoji={sceneInfo?.emoji || "🌍"}
        sceneName={sceneInfo?.label || "Scene"}
        cityName={cityName}
      />

      {/* Global Memory Logs */}
      <GlobalMemoryLogs isOpen={showMemoryLogs} onClose={() => setShowMemoryLogs(false)} />

      {/* Echo Memory Modal */}
      <EchoMemoryModal
        isOpen={showEchoMemory}
        onClose={() => setShowEchoMemory(false)}
        countryCode={countryCode}
        cityId={cityId}
        cityName={cityName}
        sceneType={scene}
        onSave={() => {
          setShowEchoMemory(false);
          toast.success("Echo Memory saved to your Global Memory Logs! 🌍");
        }}
      />
    </div>
  );
};

export default SceneArenaView;


